/* @flow weak */
import invariant from 'invariant';
import storage from 'redux-storage';
import storageDebounce from 'redux-storage-decorator-debounce';
import {APP_STORAGE_LOAD, fetchMigrationData} from './app/actions';
import {Iterable} from 'immutable';
import {fromJSON, toJSON} from './transit';

// Preserves the full storage object so we dont have to load it every update
let savedState;

const stateToSave = [
    ['app', 'location'],
    ['categories', 'waitingOrders'],
    ['categories', 'tables'],
    ['categories', 'lastBillNum'],
    ['categories', 'tablePage'],
    ['categories', 'countTablePage'],
    ['device', 'name'],
    ['device', 'id'],
    ['device', 'hideMouse'],
    ['device', 'defaultHomepage'],
    ['device', 'silentPrint'],
    ['device', 'kitchenPrint'],
    ['device', 'kitchenItemPrintSeparate'],
    ['device', 'fullScreen'],
    ['device', 'loginByPin'],
    ['device', 'logoutTimer'],
    ['device', 'organizationName'],
    ['device', 'organizationDomain'],
    ['device', 'billPrint'],
    ['device', 'printType'],
    ['device', 'printAddress'],
    ['device', 'kitchenPrintType'],
    ['device', 'kitchenPrintAddress'],
    ['device', 'printLength'],
    ['device', 'kitchenPrintLength'],
    ['users', 'lastAction'],
    ['logs', 'logs'],
    ['logs', 'appLogId'],
    ['paymentPopup', 'defaultPaymentRadio'],
    ['tables', 'defaultTablesVisible'],
    ['favourite', 'favouriteItems'],
    ['intl', 'currentLocale']
];

const isReactNative =
    typeof navigator === 'object' &&
    navigator.product === 'ReactNative'; // eslint-disable-line no-undef

if (isReactNative) {
    stateToSave.push(['app', 'location']);
}

const invariantFeatureState = (state, feature) => invariant(
    Iterable.isIterable(state[feature]),
    `Storage persists only immutable iterables. '${feature}' is something else.`
);

const updateState = (state, storageStateJson, isMigration) => {
    const empty = !storageStateJson || !Object.keys(storageStateJson).length;
    if (empty) return state;
    const activeDomain = localStorage.getItem('domain');
    const storageState = fromJSON(storageStateJson);
    const storageDomainState = isMigration ? storageState : storageState[activeDomain];
    if(!storageDomainState) return state;
    try {
        storageDomainState.forEach(({feature, featurePath, value}) => {
            const canSet = state[feature] && state[feature].hasIn(featurePath);
            if (!canSet) return;
            // As we can see, setIn always overrides the current value.
            // That's perfect for fields, currentLocale, or viewer.
            // But what if something is prefetched on the server? Then we would like
            // to merge locally cached data with fresh data from the server.
            // TODO: Add customUpdate.
            state[feature] = state[feature].setIn(featurePath, value);
        });
        state['device'] = state['device'].set('organizationDomain', activeDomain);
    } catch (error) {
        // Shouldn't happen, but if the data's invalid, there's not much we can do.
        console.log(error); // eslint-disable-line no-console
    }
    return state;
};

const storageFilter = engine => ({
    ...engine,
    save(state) {
        if (!state) return Promise.resolve();
        // We don't filter by actions but by the app state structure.
        // That's fine because saving is debounced.
        const saveState = stateToSave.map(([feature, ...featurePath]) => {
            invariantFeatureState(state, feature);
            return {
                feature, featurePath, value: state[feature].getIn(featurePath),
            };
        });
        const activeDomain = localStorage.getItem('domain');

        if(!savedState) {
            const localState = localStorage.getItem('redux-storage:ocellot-pos');
            let stateJson = JSON.parse(localState);
            if (!stateJson) stateJson = toJSON({});
            savedState = fromJSON(stateJson);
        }

        // If current state domain doesn't correspond with activeDomain, do not rewrite storage;
        // This prevents organization's saved state reload when choosing domain
        if (!activeDomain || state['device'].get('organizationDomain') !== activeDomain) return engine.save(toJSON(savedState));
        savedState[activeDomain] = saveState;
        return engine.save(toJSON(savedState));
    },
});

const createStorageMiddleware = storageEngine => {
    let decoratedEngine = storageFilter(storageEngine);
    decoratedEngine = storageDebounce(decoratedEngine, 300);
    return storage.createMiddleware(decoratedEngine);
};

export const updateStateOnStorageLoad = reducer => (state, action) => {
    if (action.type === APP_STORAGE_LOAD) {
            state = updateState(state, action.payload.state, action.payload.useMigrationData);
    }
    return reducer(state, action);
};

const configureStorage = (initialState, createStorageEngine) => {
    const storageEngine =
        createStorageEngine &&
        createStorageEngine(`redux-storage:${initialState.config.appName}`);
    const storageMiddleware =
        storageEngine &&
        createStorageMiddleware(storageEngine);

    return {
        STORAGE_SAVE: storage.SAVE,
        storageEngine,
        storageMiddleware,
    };
};

export default configureStorage;



// WEBPACK FOOTER //
// ./src/common/configureStorage.js